import React, { useEffect, useState } from 'react';
import { ArrowLeft, Package, Barcode, CheckCircle2, AlertCircle, Truck, User, MapPin, Phone, CreditCard, Info, XCircle, ChevronRight, Scan, Clock, Globe, Smartphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Order, OrderStatus } from './OrderQueue';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface OrderDetailsProps {
  order: Order;
  onBack: () => void;
  onUpdateStatus: (status: OrderStatus, updates?: Partial<Order>) => void;
  onUpdatePaymentMethod?: (paymentType: PaymentType) => void;
  readOnly?: boolean;
  initialShowRejectModal?: boolean;
  initialIsBulkScanning?: boolean;
  initialConfirmItem?: LineItem | null;
  initialIsWaitingConfirmation?: boolean;
  initialShowPartialModal?: boolean;
  initialScanningItem?: LineItem | null;
  initialShowGlobalScanModal?: boolean;
  initialShowHoldModal?: boolean;
  initialShowOtpModal?: boolean;
}
interface LotNumber {
  number: string;
  expiryDate: string;
  stock: number;
}
interface LineItem {
  id: string;
  name: string;
  sku: string;
  barcode: string;
  image: string;
  quantity: number;
  pickedQuantity: number;
  isSerialized: boolean;
  serialNumbers: string[];
  status: 'Pending' | 'Picked' | 'Partially Picked' | 'Unavailable';
  lotNumbers?: LotNumber[];
  selectedLotNumber?: string;
  requiresPrescription?: boolean;
}
export function OrderDetails({
  order,
  onBack,
  onUpdateStatus,
  initialShowRejectModal = false,
  initialIsBulkScanning = false,
  initialConfirmItem = null,
  initialIsWaitingConfirmation = false,
  initialShowPartialModal = false,
  initialScanningItem = null,
  initialShowGlobalScanModal = false,
  initialShowHoldModal = false,
  initialShowOtpModal = false,
  readOnly = false,
  onUpdatePaymentMethod
}: OrderDetailsProps) {
  const [isBulkScanning, setIsBulkScanning] = useState(initialIsBulkScanning);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [isWaitingConfirmation, setIsWaitingConfirmation] = useState(initialIsWaitingConfirmation);
  const [items, setItems] = useState<LineItem[]>([{
    id: '1',
    name: 'Panadol Extra 500mg',
    sku: 'PAN-001',
    barcode: '6281234567890',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop',
    quantity: 2,
    pickedQuantity: 0,
    isSerialized: false,
    serialNumbers: [],
    status: 'Pending',
    requiresPrescription: true,
    lotNumbers: [{
      number: 'LOT-A1',
      expiryDate: '2025-12',
      stock: 50
    }, {
      number: 'LOT-A2',
      expiryDate: '2026-06',
      stock: 30
    }]
  }, {
    id: '2',
    name: 'Amoxicillin 500mg (Serialized)',
    sku: 'AMX-500',
    barcode: '6289876543210',
    image: 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=200&h=200&fit=crop',
    quantity: 1,
    pickedQuantity: 0,
    isSerialized: true,
    serialNumbers: [],
    status: 'Pending',
    requiresPrescription: true,
    lotNumbers: [{
      number: 'LOT-B1',
      expiryDate: '2025-08',
      stock: 100
    }]
  }, {
    id: '3',
    name: 'Vitamin C 1000mg Effervescent',
    sku: 'VIT-C',
    barcode: '6285554443332',
    image: 'https://images.unsplash.com/photo-1616671285442-299c27666291?w=200&h=200&fit=crop',
    quantity: 1,
    pickedQuantity: 0,
    isSerialized: false,
    serialNumbers: [],
    status: 'Pending',
    lotNumbers: [{
      number: 'LOT-C1',
      expiryDate: '2024-11',
      stock: 10
    }, {
      number: 'LOT-C2',
      expiryDate: '2025-03',
      stock: 45
    }]
  }]);
  const [scanningItem, setScanningItem] = useState<LineItem | null>(null);
  const [showGlobalScanModal, setShowGlobalScanModal] = useState(initialShowGlobalScanModal);
  const [globalScanValue, setGlobalScanValue] = useState('');
  const [manualQty, setManualQty] = useState('');
  const [tempSerial, setTempSerial] = useState('');
  const [tempLot, setTempLot] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(initialShowRejectModal);
  const [showPartialModal, setShowPartialModal] = useState(initialShowPartialModal);
  const [showHoldModal, setShowHoldModal] = useState(initialShowHoldModal);
  const [showOtpModal, setShowOtpModal] = useState(initialShowOtpModal);
  const [showRxModal, setShowRxModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  useEffect(() => {
    let interval: any;
    if (showOtpModal && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, resendTimer]);
  const handleResendOtp = () => {
    setResendTimer(60);
    setCanResend(false);
    import('sonner').then(({
      toast
    }) => toast.success('OTP has been resent to the customer'));
  };
  const handleSendWhatsApp = () => {
    import('sonner').then(({
      toast
    }) => toast.success('OTP sent via WhatsApp successfully'));
  };
  const [rejectionReason, setRejectionReason] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  useEffect(() => {
    setShowRejectModal(initialShowRejectModal);
  }, [initialShowRejectModal]);
  useEffect(() => {
    setIsBulkScanning(initialIsBulkScanning);
  }, [initialIsBulkScanning]);
  useEffect(() => {
    setIsWaitingConfirmation(initialIsWaitingConfirmation);
  }, [initialIsWaitingConfirmation]);
  useEffect(() => {
    setShowPartialModal(initialShowPartialModal);
  }, [initialShowPartialModal]);
  useEffect(() => {
    setShowGlobalScanModal(initialShowGlobalScanModal);
  }, [initialShowGlobalScanModal]);
  useEffect(() => {
    setShowHoldModal(initialShowHoldModal);
  }, [initialShowHoldModal]);
  useEffect(() => {
    setShowOtpModal(initialShowOtpModal);
  }, [initialShowOtpModal]);
  useEffect(() => {
    if (initialScanningItem) {
      setScanningItem(initialScanningItem);
      setShowGlobalScanModal(true);
      setManualQty(initialScanningItem.pickedQuantity > 0 ? initialScanningItem.pickedQuantity.toString() : initialScanningItem.quantity.toString());
    }
  }, [initialScanningItem]);
  const rejectionReasons = ['Out of Stock', 'Prescription Invalid', 'Controlled Substance Restriction', 'Store Closing Soon', 'Delivery Area Not Covered', 'Other'];
  const handlePickItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    setScanningItem(item);
    setShowGlobalScanModal(true);
    setManualQty(item.pickedQuantity > 0 ? item.pickedQuantity.toString() : item.quantity.toString());
    setTempLot(item.selectedLotNumber || '');
  };
  const handleGlobalScan = (input: string) => {
    const parts = input.split('*');
    const scanValue = parts[0].trim();
    const scanQty = parts.length > 1 ? parseInt(parts[1]) : 1;
    const item = items.find(i => i.sku === scanValue || i.isSerialized && scanValue.startsWith('SN-') || i.lotNumbers && i.lotNumbers.some(l => l.number === scanValue));
    if (item) {
      setScanningItem(item);
      setManualQty(item.pickedQuantity > 0 ? Math.min(item.pickedQuantity + scanQty, item.quantity).toString() : Math.min(scanQty, item.quantity).toString());
      if (item.isSerialized && scanValue.startsWith('SN-')) {
        setTempSerial(scanValue);
      }
      if (item.lotNumbers?.some(l => l.number === scanValue)) {
        setTempLot(scanValue);
      }
      import('sonner').then(({
        toast
      }) => {
        toast.success(`Item Identified: ${item.name}`);
      });
    } else {
      import('sonner').then(({
        toast
      }) => {
        toast.error(`Item ${scanValue} not found in this order`);
      });
    }
    setGlobalScanValue('');
  };
  const handleConfirmItemFulfillment = () => {
    if (!scanningItem) return;
    const qty = parseInt(manualQty) || 0;
    const finalQty = Math.min(qty, scanningItem.quantity);
    setItems(prev => prev.map(i => i.id === scanningItem.id ? {
      ...i,
      pickedQuantity: finalQty,
      selectedLotNumber: tempLot,
      serialNumbers: tempSerial && i.isSerialized && !i.serialNumbers.includes(tempSerial) ? [...i.serialNumbers, tempSerial] : i.serialNumbers,
      status: finalQty === i.quantity ? 'Picked' : finalQty > 0 ? 'Partially Picked' : 'Pending'
    } : i));
    // Clear and return to scan state
    setScanningItem(null);
    setTempSerial('');
    setTempLot('');
    setManualQty('');
  };
  const handleScanAll = () => {
    setIsWaitingConfirmation(true);
    import('sonner').then(({
      toast
    }) => {
      toast.info('Waiting for items scan confirmation...');
    });
  };
  const handleConfirmScanAll = () => {
    setItems(prev => prev.map(item => ({
      ...item,
      pickedQuantity: item.quantity,
      status: 'Picked',
      serialNumbers: item.isSerialized && item.serialNumbers.length === 0 ? [`SN-AUTO-${Math.random().toString(36).substring(2, 9).toUpperCase()}`] : item.serialNumbers
    })));
    setIsWaitingConfirmation(false);
    import('sonner').then(({
      toast
    }) => {
      toast.success('All items confirmed and marked as picked');
    });
  };
  const allPicked = items.every(i => i.status === 'Picked' || i.status === 'Unavailable');
  const canComplete = allPicked || items.some(i => i.status === 'Partially Picked' || i.status === 'Picked');
  return <div className="max-w-6xl mx-auto space-y-6 pb-20" data-id="element-9">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-10">
        <div className="flex items-center gap-4" data-id="element-11">
          <button onClick={onBack} className="p-2 hover:bg-white rounded-full transition-colors border border-transparent hover:border-slate-200" data-id="element-12">
            <ArrowLeft className="w-5 h-5 text-slate-600" data-id="element-13" />
          </button>
          <div data-id="element-14">
            <div className="flex items-center gap-3" data-id="element-15">
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-16">
                Order #{order.id}
              </h2>
              <div className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border', order.paymentType === 'COD' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100')} data-id="element-17">
                {order.paymentType}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-0.5" data-id="element-18">
              <p className="text-slate-500 text-xs" data-id="element-19">
                Placed on {order.createdAt}
              </p>
              <div className="w-1 h-1 bg-slate-300 rounded-full" data-id="element-20" />
              <p className="text-slate-500 text-xs" data-id="element-21">
                Source:{' '}
                <span className="font-bold text-slate-700" data-id="element-22">
                  {order.channel}
                </span>
              </p>
              {order.sourceOrderId && <>
                  <div className="w-1 h-1 bg-slate-300 rounded-full" data-id="element-23" />
                  <p className="text-slate-500 text-xs" data-id="element-24">
                    Ext ID:{' '}
                    <span className="font-bold text-slate-700" data-id="element-25">
                      {order.sourceOrderId}
                    </span>
                  </p>
                </>}
            </div>
          </div>
        </div>

        {!readOnly && <div className="flex items-center gap-3" data-id="element-26">
            {/* Pharmacist Permissions: Cancel anytime before pickup */}
            {['Pending', 'Accepted', 'Picking', 'Searching for Driver', 'Driver Assigned', 'Ready for Pickup', 'Out for Delivery', 'Delivery Attempt Failed'].includes(order.status) && <div className="flex items-center gap-2" data-id="element-27">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-28">
                  Cancel Order
                </button>
                <button onClick={() => setShowHoldModal(true)} className="px-4 py-2 text-slate-600 font-bold text-sm hover:bg-slate-50 rounded-xl transition-colors" data-id="element-29">
                  Hold Order
                </button>
              </div>}

            {order.status === 'Held' && <div className="flex gap-3" data-id="element-30">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-31">
                  Cancel Order
                </button>
                <button onClick={() => onUpdateStatus(order.previousStatus || 'Pending')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all" data-id="element-32">
                  Resume Order
                </button>
              </div>}

            {order.status === 'Pending' && <div className="flex items-center gap-3" data-id="element-33">
                {order.requiresPrescription && !order.prescriptionVerified && <button onClick={() => setShowRxModal(true)} className="px-6 py-2 bg-purple-600 text-white font-bold rounded-xl shadow-lg shadow-purple-200 hover:bg-purple-700 transition-all flex items-center gap-2" data-id="element-34">
                    <Info className="w-4 h-4" data-id="element-35" />
                    Verify Prescription
                  </button>}
                <button disabled={order.requiresPrescription && !order.prescriptionVerified} onClick={() => onUpdateStatus('Accepted')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all disabled:opacity-50 disabled:shadow-none" data-id="element-36">
                  Accept Order
                </button>
              </div>}

            {order.status === 'Accepted' && <button onClick={() => onUpdateStatus('Picking')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-37">
                <Package className="w-4 h-4" data-id="element-38" />
                Start Picking
              </button>}

            {order.status === 'Picking' && <>
                {/* Ready to Pick only shows after scanning is finalized */}
                {allPicked ? <button onClick={() => {
            if (order.isPickup) {
              onUpdateStatus('Ready for Pickup');
            } else {
              onUpdateStatus('Searching for Driver');
            }
          }} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all flex items-center gap-2" data-id="element-39">
                    <CheckCircle2 className="w-4 h-4" data-id="element-40" />
                    Ready to Pick
                  </button> : <button onClick={() => setShowPartialModal(true)} className="px-6 py-2 bg-amber-500 text-white font-bold rounded-xl shadow-lg shadow-amber-200 hover:bg-amber-600 transition-all flex items-center gap-2" data-id="element-41">
                    <CheckCircle2 className="w-4 h-4" data-id="element-42" />
                    Confirm Scan
                  </button>}
              </>}

            {order.status === 'Searching for Driver' && <button onClick={() => onUpdateStatus('Driver Assigned', {
          driverInfo: {
            name: 'Ahmed Al-Fahad',
            phone: '+966 55 000 1111',
            vehiclePlate: 'ABC 1234',
            rating: 4.8,
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ahmed'
          }
        })} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-43">
                <Truck className="w-4 h-4" data-id="element-44" />
                Simulate Driver Assignment
              </button>}

            {order.status === 'Driver Assigned' && <button onClick={() => onUpdateStatus('Out for Delivery')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-45">
                <Truck className="w-4 h-4" data-id="element-46" />
                Driver Picked Up
              </button>}

            {order.status === 'Out for Delivery' && <div className="flex gap-3" data-id="element-47">
                <button onClick={() => onUpdateStatus('Delivery Attempt Failed')} className="px-4 py-2 text-rose-600 font-bold text-sm hover:bg-rose-50 rounded-xl transition-colors" data-id="element-48">
                  Delivery Failed
                </button>
                <button onClick={() => {
            if (order.paymentType === 'COD') {
              import('sonner').then(({
                toast
              }) => toast.success('COD Payment Confirmed & Collected'));
            }
            onUpdateStatus('Delivered');
          }} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all" data-id="element-49">
                  {order.paymentType === 'COD' ? 'Confirm Payment & Delivery' : 'Confirm Delivery'}
                </button>
              </div>}

            {order.status === 'Delivery Attempt Failed' && <div className="flex gap-3" data-id="element-50">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-51">
                  Cancel Order
                </button>
                <button onClick={() => setShowHoldModal(true)} className="px-6 py-2 bg-slate-600 text-white font-bold rounded-xl shadow-lg shadow-slate-200 hover:bg-slate-700 transition-all" data-id="element-52">
                  Hold Order
                </button>
              </div>}

            {order.status === 'Ready for Pickup' && <button onClick={() => setShowOtpModal(true)} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-53">
                <User className="w-4 h-4" data-id="element-54" />
                Customer Pickup
              </button>}

            {order.status === 'Delivered' && !readOnly && <button onClick={() => onUpdateStatus('Return Requested')} className="px-6 py-2 bg-rose-600 text-white font-bold rounded-xl shadow-lg shadow-rose-200 hover:bg-rose-700 transition-all" data-id="element-55">
                Initiate Return
              </button>}

            {order.status === 'Return Requested' && !readOnly && <button onClick={() => onUpdateStatus('Refunded')} className="px-6 py-2 bg-slate-800 text-white font-bold rounded-xl shadow-lg shadow-slate-200 hover:bg-slate-900 transition-all" data-id="element-56">
                Complete Refund
              </button>}
          </div>}
      </div>

      <div className="grid grid-cols-3 gap-6" data-id="element-57">
        {/* Left Column: Items */}
        <div className="col-span-2 space-y-6" data-id="element-58">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-59">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-60">
              <div className="flex items-center gap-4" data-id="element-61">
                <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-62">
                  <Package className="w-4 h-4 text-blue-600" data-id="element-63" />
                  Order Items ({items.length})
                </h3>
                {order.status === 'Picking' && <button onClick={() => {
                setShowGlobalScanModal(true);
                setIsBulkScanning(true);
              }} className={cn('flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold transition-all', isBulkScanning ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')} data-id="element-64">
                    <Scan className="w-3.5 h-3.5" data-id="element-65" />
                    {isBulkScanning ? 'Bulk Scan Active' : 'Enable Bulk Scan'}
                  </button>}
              </div>
              {order.status === 'Picking' && <div className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded" data-id="element-66">
                  {items.filter(i => i.status === 'Picked').length} /{' '}
                  {items.length} Fully Picked
                </div>}
            </div>

            <div className="divide-y divide-slate-100" data-id="element-67">
              {items.map(item => <div key={item.id} className={cn('p-6 flex items-center justify-between group transition-colors', item.status === 'Picked' && 'bg-emerald-50/30')} data-id="element-68">
                  <div className="flex gap-4" data-id="element-69">
                    <div className="relative" data-id="element-70">
                      <img src={item.image} alt={item.name} className={cn('w-16 h-16 rounded-xl object-cover border', item.status === 'Picked' ? 'border-emerald-200' : 'border-slate-200')} data-id="element-71" />
                      {item.status === 'Picked' && <div className="absolute -top-2 -right-2 bg-emerald-500 text-white rounded-full p-0.5 border-2 border-white" data-id="element-72">
                          <CheckCircle2 className="w-3.5 h-3.5" data-id="element-73" />
                        </div>}
                    </div>
                    <div data-id="element-74">
                      <p className="font-bold text-slate-900" data-id="element-75">{item.name}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1" data-id="element-76">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-77">
                          SKU:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-78">
                            {item.sku}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-79">
                          Barcode:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-80">
                            {item.barcode}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-81">
                          Qty:{' '}
                          <span className="text-blue-600" data-id="element-82">
                            {item.pickedQuantity} / {item.quantity}
                          </span>
                        </p>
                        {item.isSerialized && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase" data-id="element-83">
                            <Barcode className="w-3 h-3" data-id="element-84" />
                            Serialized
                          </div>}
                        {item.requiresPrescription && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-bold uppercase border border-purple-100" data-id="element-85">
                            <Info className="w-3 h-3" data-id="element-86" />
                            Rx Required
                          </div>}
                        {item.selectedLotNumber && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-bold uppercase border border-amber-100" data-id="element-87">
                            <Info className="w-3 h-3" data-id="element-88" />
                            Lot: {item.selectedLotNumber}
                          </div>}
                      </div>
                      {item.serialNumbers.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-89">
                          {item.serialNumbers.map(sn => <p key={sn} className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100" data-id="element-90">
                              SN: {sn}
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {order.status === 'Picking' && <button onClick={() => handlePickItem(item.id)} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold hover:border-blue-500 hover:text-blue-600 transition-all flex items-center gap-2" data-id="element-91">
                      {item.isSerialized ? <Scan className="w-4 h-4" data-id="element-92" /> : <CheckCircle2 className="w-4 h-4" data-id="element-93" />}
                      {item.status === 'Picked' ? 'Edit' : item.isSerialized ? 'Scan Serial' : 'Confirm Qty'}
                    </button>}
                </div>)}
            </div>
          </div>

          {/* Order Timeline / History */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-94">
            <div className="flex items-center justify-between mb-6" data-id="element-95">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-96">
                <Clock className="w-4 h-4 text-blue-600" data-id="element-97" />
                Full Order Audit Trail
              </h3>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded border border-slate-100" data-id="element-98">
                {order.history?.length || 0} Events Logged
              </span>
            </div>

            <div className="space-y-0 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100" data-id="element-99">
              {order.history?.map((event, idx) => {
              const isLast = idx === order.history.length - 1;
              return <div key={event.id} className={cn('relative pl-12 pb-8', isLast && 'pb-0')} data-id="element-100">
                    {/* Timeline Node */}
                    <div className={cn('absolute left-0 top-0 w-10 h-10 rounded-full flex items-center justify-center border-4 border-white shadow-sm transition-all z-10', isLast ? 'bg-blue-600 text-white ring-4 ring-blue-50' : 'bg-white text-slate-400 border-slate-100')} data-id="element-101">
                      {event.status === 'Pending' && <Globe className="w-4 h-4" data-id="element-102" />}
                      {event.status === 'Accepted' && <CheckCircle2 className="w-4 h-4" data-id="element-103" />}
                      {event.status === 'Picking' && <Package className="w-4 h-4" data-id="element-104" />}
                      {event.status === 'Searching for Driver' && <Truck className="w-4 h-4" data-id="element-105" />}
                      {event.status === 'Driver Assigned' && <User className="w-4 h-4" data-id="element-106" />}
                      {event.status === 'Out for Delivery' && <Truck className="w-4 h-4" data-id="element-107" />}
                      {event.status === 'Delivered' && <CheckCircle2 className="w-4 h-4" data-id="element-108" />}
                      {event.status === 'Cancelled' && <XCircle className="w-4 h-4" data-id="element-109" />}
                      {event.status === 'Rejected' && <XCircle className="w-4 h-4" data-id="element-110" />}
                      {event.status === 'Held' && <Clock className="w-4 h-4" data-id="element-111" />}
                      {event.status === 'Ready for Pickup' && <Package className="w-4 h-4" data-id="element-112" />}
                    </div>

                    <div className={cn('p-4 rounded-2xl border transition-all', isLast ? 'bg-blue-50/30 border-blue-100' : 'bg-slate-50/30 border-transparent hover:border-slate-100')} data-id="element-113">
                      <div className="flex items-start justify-between gap-4" data-id="element-114">
                        <div className="space-y-1" data-id="element-115">
                          <div className="flex items-center gap-2" data-id="element-116">
                            <p className={cn('text-sm font-bold', isLast ? 'text-blue-700' : 'text-slate-900')} data-id="element-117">
                              {event.status}
                            </p>
                            <span className={cn('px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter', event.actor === 'System' ? 'bg-slate-100 text-slate-500' : event.actor === 'Pharmacist' ? 'bg-blue-100 text-blue-600' : event.actor === 'Driver' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600')} data-id="element-118">
                              {event.actor}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 leading-relaxed" data-id="element-119">
                            {event.message}
                          </p>
                        </div>
                        <div className="text-right shrink-0" data-id="element-120">
                          <p className="text-[10px] font-bold text-slate-400 tabular-nums" data-id="element-121">
                            {event.timestamp.split(' ')[1]}{' '}
                            {event.timestamp.split(' ')[2]}
                          </p>
                          <p className="text-[9px] font-medium text-slate-300 uppercase" data-id="element-122">
                            {event.timestamp.split(' ')[0]}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-slate-100/50 flex items-center gap-2" data-id="element-123">
                        <div className="w-5 h-5 rounded-full bg-white border border-slate-100 flex items-center justify-center overflow-hidden" data-id="element-124">
                          <User className="w-3 h-3 text-slate-400" data-id="element-125" />
                        </div>
                        <p className="text-[10px] font-bold text-slate-500" data-id="element-126">
                          Action by:{' '}
                          <span className="text-slate-700" data-id="element-127">
                            {event.actorName}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Right Column: Info */}
        <div className="space-y-6" data-id="element-128">
          {/* Order Source Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-129">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-130">
              <Globe className="w-4 h-4 text-blue-600" data-id="element-131" />
              Order Source
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-132">
              <div data-id="element-133">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-134">
                  Source
                </p>
                <div className="flex items-center gap-2" data-id="element-135">
                  {order.channel === 'Website' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold uppercase" data-id="element-136">
                      <Globe className="w-3 h-3" data-id="element-137" />
                      Website
                    </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold uppercase" data-id="element-138">
                      <Smartphone className="w-3 h-3" data-id="element-139" />
                      Aggregator
                    </div>}
                </div>
              </div>
              <div data-id="element-140">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-141">
                  Source ID
                </p>
                <p className="text-sm font-bold text-slate-900" data-id="element-142">
                  {order.sourceOrderId || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-143">
            <div className="flex items-center justify-between mb-4" data-id="element-144">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-145">
                <User className="w-4 h-4 text-blue-600" data-id="element-146" />
                Customer Profile
              </h3>
              {order.isLoyaltyCustomer && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-black uppercase tracking-widest border border-amber-200" data-id="element-147">
                  Loyalty Member
                </span>}
            </div>
            <div className="space-y-4" data-id="element-148">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-149">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-150">
                  <User className="w-5 h-5 text-slate-400" data-id="element-151" />
                </div>
                <div data-id="element-152">
                  <p className="text-sm font-bold text-slate-900" data-id="element-153">
                    {order.customerName}
                  </p>
                  <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider" data-id="element-154">
                    {order.customerGender || 'N/A'} •{' '}
                    {order.customerAge || 'N/A'} Years
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-155">
                <div className="space-y-1" data-id="element-156">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-157">
                    City
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-158">
                    {order.customerCity || 'N/A'}
                  </p>
                </div>
                <div className="space-y-1" data-id="element-159">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-160">
                    District
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-161">
                    {order.customerDistrict || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="space-y-1 pt-2 border-t border-slate-50" data-id="element-162">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-163">
                  <MapPin className="w-3 h-3" data-id="element-164" /> Address
                </p>
                <p className="text-xs text-slate-600 leading-relaxed" data-id="element-165">
                  {order.customerAddress}
                </p>
              </div>

              <div className="space-y-1" data-id="element-166">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-167">
                  <Phone className="w-3 h-3" data-id="element-168" /> Phone
                </p>
                <p className="text-xs font-bold text-blue-600" data-id="element-169">
                  {order.customerPhone}
                </p>
              </div>
            </div>
          </div>

          {/* Shipping & Courier Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-170">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-171">
              <Truck className="w-4 h-4 text-blue-600" data-id="element-172" />
              Shipping Details
            </h3>
            <div className="space-y-4" data-id="element-173">
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-174">
                <span className="text-xs font-bold text-slate-500 uppercase" data-id="element-175">
                  Method
                </span>
                <span className="text-sm font-black text-blue-600 uppercase tracking-tight" data-id="element-176">
                  {order.isPickup ? 'Self Pickup' : 'Home Delivery'}
                </span>
              </div>

              {!order.isPickup && <div className="space-y-4" data-id="element-177">
                  <div className="flex justify-between items-center" data-id="element-178">
                    <span className="text-xs font-bold text-slate-400 uppercase" data-id="element-179">
                      Courier
                    </span>
                    <div className="flex items-center gap-2" data-id="element-180">
                      <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center" data-id="element-181">
                        <Globe className="w-3 h-3 text-slate-500" data-id="element-182" />
                      </div>
                      <span className="text-sm font-bold text-slate-900" data-id="element-183">
                        {order.courierName || 'Not Assigned'}
                      </span>
                    </div>
                  </div>

                  {order.driverInfo && <div className="pt-4 border-t border-slate-100" data-id="element-184">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-3" data-id="element-185">
                        Assigned Driver
                      </p>
                      <div className="flex items-center gap-3" data-id="element-186">
                        <img src={order.driverInfo.avatar} alt={order.driverInfo.name} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" data-id="element-187" />
                        <div className="flex-1" data-id="element-188">
                          <p className="text-sm font-bold text-slate-900" data-id="element-189">
                            {order.driverInfo.name}
                          </p>
                          <p className="text-[10px] text-slate-500 font-mono" data-id="element-190">
                            {order.driverInfo.vehiclePlate}
                          </p>
                        </div>
                        <button className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-191">
                          <Phone className="w-4 h-4" data-id="element-192" />
                        </button>
                      </div>
                    </div>}
                </div>}

              {order.status === 'Searching for Driver' && <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl flex items-center gap-3 animate-pulse" data-id="element-193">
                  <div className="w-2 h-2 bg-blue-600 rounded-full" data-id="element-194"></div>
                  <span className="text-[10px] font-bold text-blue-700 uppercase" data-id="element-195">
                    Requesting Driver...
                  </span>
                </div>}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-196">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-197">
              <CreditCard className="w-4 h-4 text-blue-600" data-id="element-198" />
              Payment & Loyalty
            </h3>
            <div className="space-y-4" data-id="element-199">
              {/* Main Totals */}
              <div className="space-y-2" data-id="element-200">
                <div className="flex justify-between text-sm" data-id="element-201">
                  <span className="text-slate-500" data-id="element-202">Subtotal</span>
                  <span className="font-medium text-slate-900" data-id="element-203">
                    {((order.totalAmount + (order.discountAmount || 0)) / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-204">
                  <span className="text-slate-500" data-id="element-205">VAT (15%)</span>
                  <span className="font-medium text-slate-900" data-id="element-206">
                    {((order.totalAmount + (order.discountAmount || 0)) * 0.15 / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
              </div>

              {/* Discounts & Loyalty Breakdown */}
              {(order.discountAmount || order.loyaltyPointsUsed || order.qitafAmount || order.walletAmount) && <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 space-y-2" data-id="element-207">
                  {order.discountAmount && <div className="flex justify-between text-xs" data-id="element-208">
                      <div className="flex items-center gap-2" data-id="element-209">
                        <span className="text-emerald-700 font-bold uppercase" data-id="element-210">
                          Coupon
                        </span>
                        <span className="px-1.5 py-0.5 bg-white text-emerald-700 rounded text-[9px] font-black border border-emerald-200 uppercase" data-id="element-211">
                          {order.couponCode}
                        </span>
                      </div>
                      <span className="font-bold text-emerald-700" data-id="element-212">
                        -{order.discountAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.loyaltyPointsUsed && <div className="flex justify-between text-xs" data-id="element-213">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-214">
                        Loyalty Points ({order.loyaltyPointsUsed})
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-215">
                        Redeemed
                      </span>
                    </div>}
                  {order.qitafAmount && <div className="flex justify-between text-xs" data-id="element-216">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-217">
                        Qitaf Points
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-218">
                        -{order.qitafAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.walletAmount && <div className="flex justify-between text-xs" data-id="element-219">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-220">
                        Wallet Balance
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-221">
                        -{order.walletAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                </div>}

              <div className="pt-3 border-t border-slate-100 flex justify-between items-center" data-id="element-222">
                <span className="font-bold text-slate-900" data-id="element-223">Grand Total</span>
                <span className="text-lg font-black text-blue-600" data-id="element-224">
                  {order.totalAmount.toFixed(2)} SAR
                </span>
              </div>

              {/* Payment Method Selector */}
              <div className="mt-4 space-y-3" data-id="element-225">
                <div className={cn('p-3 rounded-xl flex items-center justify-between border', order.paymentType === 'COD' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100')} data-id="element-226">
                  <div className="flex items-center gap-2" data-id="element-227">
                    <Info className={cn('w-4 h-4', order.paymentType === 'COD' ? 'text-amber-600' : 'text-blue-600')} data-id="element-228" />
                    <div className="flex flex-col" data-id="element-229">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter" data-id="element-230">
                        Payment Method
                      </span>
                      <span className={cn('text-xs font-black uppercase', order.paymentType === 'COD' ? 'text-amber-700' : 'text-blue-700')} data-id="element-231">
                        {order.paymentType}
                      </span>
                    </div>
                  </div>
                  {!readOnly && !['Out for Delivery', 'Delivered', 'Delivery Attempt Failed'].includes(order.status) && <button onClick={() => setIsEditingPayment(!isEditingPayment)} className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-blue-600 hover:border-blue-500 uppercase shadow-sm transition-all" data-id="element-232">
                        {isEditingPayment ? 'Cancel' : 'Change'}
                      </button>}
                </div>

                {isEditingPayment && <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded-xl border border-slate-100 animate-in fade-in slide-in-from-top-1" data-id="element-233">
                    {['COD', 'Visa', 'Mada', 'ApplePay', 'Tamara', 'Tabby'].map(method => <button key={method} onClick={() => {
                  onUpdatePaymentMethod?.(method as PaymentType);
                  setIsEditingPayment(false);
                }} className={cn('px-3 py-2 rounded-lg text-[10px] font-bold uppercase transition-all', order.paymentType === method ? 'bg-blue-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300')} data-id="element-234">
                          {method}
                        </button>)}
                  </div>}
              </div>
            </div>
          </div>

          {/* Driver Information */}
          {order.driverInfo && <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-235">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-236">
                <Truck className="w-4 h-4 text-blue-600" data-id="element-237" />
                Driver Information
              </h3>
              <div className="flex items-center gap-4 mb-4" data-id="element-238">
                <img src={order.driverInfo.avatar} alt={order.driverInfo.name} className="w-12 h-12 rounded-full border-2 border-blue-100" data-id="element-239" />
                <div data-id="element-240">
                  <p className="font-bold text-slate-900" data-id="element-241">
                    {order.driverInfo.name}
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-242">
                    {order.driverInfo.vehiclePlate}
                  </p>
                </div>
              </div>
              <div className="space-y-2" data-id="element-243">
                <div className="flex justify-between text-xs" data-id="element-244">
                  <span className="text-slate-500" data-id="element-245">Phone</span>
                  <span className="font-bold text-slate-900" data-id="element-246">
                    {order.driverInfo.phone}
                  </span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-247">
                  <span className="text-slate-500" data-id="element-248">Rating</span>
                  <span className="font-bold text-blue-600" data-id="element-249">
                    ★ {order.driverInfo.rating}
                  </span>
                </div>
              </div>
            </div>}

          {/* Pickup OTP Info */}
          {order.isPickup && order.status === 'Ready for Pickup' && <div className="bg-purple-600 rounded-2xl p-6 text-white shadow-lg shadow-purple-200" data-id="element-250">
              <h3 className="font-bold mb-4 flex items-center gap-2" data-id="element-251">
                <Smartphone className="w-5 h-5" data-id="element-252" />
                Pickup Verification
              </h3>
              <p className="text-purple-100 text-xs mb-4" data-id="element-253">
                Customer has received an OTP for verification. Ask the customer
                for the code upon arrival.
              </p>
              <div className="bg-purple-700/50 rounded-xl p-3 text-center" data-id="element-254">
                <p className="text-[10px] font-bold uppercase text-purple-200 mb-1" data-id="element-255">
                  Internal OTP Reference
                </p>
                <p className="text-2xl font-black tracking-widest" data-id="element-256">
                  {order.otp}
                </p>
              </div>
            </div>}

          {/* Failed Delivery / Hold Info */}
          {order.status === 'Held' && <div className="bg-slate-800 rounded-2xl p-6 text-white shadow-lg" data-id="element-257">
              <h3 className="font-bold mb-4 flex items-center gap-2 text-amber-400" data-id="element-258">
                <AlertCircle className="w-5 h-5" data-id="element-259" />
                Order on Hold
              </h3>
              <p className="text-slate-300 text-xs mb-4" data-id="element-260">
                This order is currently on hold. You can resume it to continue
                the workflow from the previous stage.
              </p>
              <div className="bg-slate-700 rounded-xl p-3 border border-slate-600" data-id="element-261">
                <p className="text-[10px] font-bold uppercase text-slate-400 mb-1" data-id="element-262">
                  Hold Reason
                </p>
                <p className="text-sm font-medium" data-id="element-263">{order.holdReason}</p>
              </div>
              {!readOnly && <button onClick={() => onUpdateStatus(order.previousStatus || 'Pending')} className="w-full mt-4 py-4 bg-emerald-600 text-white font-black uppercase tracking-widest text-xs rounded-2xl hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-900/20 flex items-center justify-center gap-2 group" data-id="element-264">
                  <CheckCircle2 className="w-4 h-4 group-hover:scale-110 transition-transform" data-id="element-265" />
                  Resume Order (Unhold)
                </button>}
            </div>}
        </div>
      </div>

      {/* Continuous Picking Modal */}
      <AnimatePresence data-id="element-266">
        {showGlobalScanModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-267">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-268">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-269">
                <h3 className="font-bold text-slate-900" data-id="element-270">
                  {scanningItem ? scanningItem.isSerialized ? 'Serial Registration' : 'Confirm Quantity' : 'Continuous Picking'}
                </h3>
                <button onClick={() => {
              setShowGlobalScanModal(false);
              setScanningItem(null);
              setIsBulkScanning(false);
            }} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-271">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-272" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-273">
                {!scanningItem ? <div className="space-y-6" data-id="element-274">
                    <div className="text-center space-y-2" data-id="element-275">
                      <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto text-blue-600" data-id="element-276">
                        <Scan className="w-10 h-10" data-id="element-277" />
                      </div>
                      <h4 className="font-bold text-lg text-slate-900" data-id="element-278">
                        Ready to Scan
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-279">
                        Scan an item's barcode or serial number to begin
                        fulfillment.
                      </p>
                    </div>

                    <div className="space-y-2" data-id="element-280">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-281">
                        Scan Item
                      </label>
                      <div className="relative" data-id="element-282">
                        <Scan className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600" data-id="element-283" />
                        <input autoFocus type="text" value={globalScanValue} onChange={e => setGlobalScanValue(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleGlobalScan(globalScanValue);
                    }
                  }} placeholder="Scan barcode, SKU, or Lot..." className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-100 focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all font-mono font-bold" data-id="element-284" />
                      </div>
                    </div>
                  </div> : <div className="space-y-6" data-id="element-285">
                    <div className="text-center space-y-2" data-id="element-286">
                      <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto text-blue-600" data-id="element-287">
                        {scanningItem.isSerialized ? <Barcode className="w-10 h-10" data-id="element-288" /> : <Package className="w-10 h-10" data-id="element-289" />}
                      </div>
                      <h4 className="font-bold text-lg text-slate-900" data-id="element-290">
                        {scanningItem.name}
                      </h4>
                      <div className="flex flex-col gap-1" data-id="element-291">
                        <p className="text-sm text-slate-500" data-id="element-292">
                          SKU:{' '}
                          <span className="font-mono font-bold" data-id="element-293">
                            {scanningItem.sku}
                          </span>
                        </p>
                        <div className="flex justify-center gap-4 mt-2" data-id="element-294">
                          <div className="text-center" data-id="element-295">
                            <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-296">
                              Ordered
                            </p>
                            <p className="font-bold text-slate-900" data-id="element-297">
                              {scanningItem.quantity}
                            </p>
                          </div>
                          <div className="w-px h-8 bg-slate-100" data-id="element-298" />
                          <div className="text-center" data-id="element-299">
                            <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-300">
                              Picked
                            </p>
                            <p className="font-bold text-emerald-600" data-id="element-301">
                              {scanningItem.pickedQuantity}
                            </p>
                          </div>
                          <div className="w-px h-8 bg-slate-100" data-id="element-302" />
                          <div className="text-center" data-id="element-303">
                            <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-304">
                              Remaining
                            </p>
                            <p className="font-bold text-blue-600" data-id="element-305">
                              {scanningItem.quantity - scanningItem.pickedQuantity}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {scanningItem.isSerialized && <div className="space-y-2" data-id="element-306">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-307">
                          Scan or Enter Serial Number
                        </label>
                        <div className="relative" data-id="element-308">
                          <Scan className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600" data-id="element-309" />
                          <input autoFocus type="text" value={tempSerial} onChange={e => setTempSerial(e.target.value)} placeholder="SN-XXXX-XXXX" className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-100 focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all font-mono font-bold" data-id="element-310" />
                        </div>
                      </div>}

                    {scanningItem.lotNumbers && scanningItem.lotNumbers.length > 0 && <div className="space-y-2" data-id="element-311">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-312">
                            Select Lot Number
                          </label>
                          <div className="grid grid-cols-1 gap-2" data-id="element-313">
                            {scanningItem.lotNumbers.map(lot => <button key={lot.number} onClick={() => setTempLot(lot.number)} className={cn('flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left', tempLot === lot.number ? 'border-blue-600 bg-blue-50' : 'border-slate-100 bg-slate-50 hover:border-slate-200')} data-id="element-314">
                                <div data-id="element-315">
                                  <p className="text-sm font-bold text-slate-900" data-id="element-316">
                                    {lot.number}
                                  </p>
                                  <p className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-317">
                                    Exp: {lot.expiryDate}
                                  </p>
                                </div>
                                <div className="text-right" data-id="element-318">
                                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-319">
                                    Stock
                                  </p>
                                  <p className="text-xs font-bold text-slate-700" data-id="element-320">
                                    {lot.stock}
                                  </p>
                                </div>
                              </button>)}
                          </div>
                        </div>}

                    <div className="space-y-2" data-id="element-321">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-322">
                        Substitution (Optional)
                      </label>
                      <button className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl text-xs font-bold text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-all flex items-center justify-between group" data-id="element-323">
                        <div className="flex items-center gap-2" data-id="element-324">
                          <Package className="w-4 h-4" data-id="element-325" />
                          <span data-id="element-326">Suggest Alternative SKU</span>
                        </div>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-327" />
                      </button>
                    </div>

                    <div className="space-y-2" data-id="element-328">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-329">
                        Confirm Picked Quantity
                      </label>
                      <div className="flex items-center gap-4" data-id="element-330">
                        <button onClick={() => setManualQty(Math.max(0, parseInt(manualQty) - 1).toString())} className="w-12 h-12 rounded-xl border-2 border-slate-100 flex items-center justify-center text-xl font-bold hover:bg-slate-50" data-id="element-331">
                          -
                        </button>
                        <input type="number" value={manualQty} onChange={e => setManualQty(e.target.value)} className="flex-1 text-center py-4 bg-slate-50 border-2 border-slate-100 focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all font-bold text-xl" data-id="element-332" />
                        <button onClick={() => setManualQty(Math.min(scanningItem.quantity, parseInt(manualQty) + 1).toString())} className="w-12 h-12 rounded-xl border-2 border-slate-100 flex items-center justify-center text-xl font-bold hover:bg-slate-50" data-id="element-333">
                          +
                        </button>
                      </div>
                      {parseInt(manualQty) < scanningItem.quantity && <p className="text-xs text-amber-600 font-medium mt-2 flex items-center gap-1" data-id="element-334">
                          <AlertCircle className="w-3 h-3" data-id="element-335" />
                          Partial fulfillment:{' '}
                          {scanningItem.quantity - parseInt(manualQty)} units
                          pending
                        </p>}
                    </div>
                  </div>}

                <div className="flex gap-3 pt-2" data-id="element-336">
                  <button onClick={() => {
                if (scanningItem) {
                  setScanningItem(null);
                  setTempSerial('');
                  setManualQty('');
                } else {
                  setShowGlobalScanModal(false);
                  setIsBulkScanning(false);
                }
              }} className="flex-1 py-4 text-slate-600 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-337">
                    {scanningItem ? 'Back to Scan' : 'Close'}
                  </button>
                  <button disabled={!scanningItem || scanningItem.isSerialized && !tempSerial || scanningItem.lotNumbers && scanningItem.lotNumbers.length > 0 && !tempLot} onClick={handleConfirmItemFulfillment} className="flex-1 py-4 bg-blue-600 text-white font-bold rounded-2xl shadow-lg shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 disabled:shadow-none transition-all" data-id="element-338">
                    Confirm & Next Scan
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Partial Fulfillment Confirmation Modal */}
      <AnimatePresence data-id="element-339">
        {showPartialModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-340">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-341">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-342">
                <h3 className="font-bold text-slate-900" data-id="element-343">
                  Partial Fulfillment
                </h3>
                <button onClick={() => setShowPartialModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-344">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-345" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-346">
                <div className="text-center space-y-2" data-id="element-347">
                  <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto text-amber-600" data-id="element-348">
                    <AlertCircle className="w-8 h-8" data-id="element-349" />
                  </div>
                  <h4 className="font-bold text-lg text-slate-900" data-id="element-350">
                    Items Still Pending
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-351">
                    Some items in this order have not been fully scanned or
                    picked.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-2xl p-4 space-y-3 max-h-[200px] overflow-auto border border-slate-100" data-id="element-352">
                  {items.filter(i => i.status !== 'Picked').map(item => <div key={item.id} className="flex justify-between items-center text-sm" data-id="element-353">
                        <div className="flex flex-col" data-id="element-354">
                          <span className="font-bold text-slate-900" data-id="element-355">
                            {item.sku}
                          </span>
                          <span className="text-xs text-slate-500 truncate max-w-[200px]" data-id="element-356">
                            {item.name}
                          </span>
                        </div>
                        <div className="text-right" data-id="element-357">
                          <span className="text-amber-600 font-bold" data-id="element-358">
                            {item.pickedQuantity} / {item.quantity}
                          </span>
                          <p className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-359">
                            Pending
                          </p>
                        </div>
                      </div>)}
                </div>

                <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl" data-id="element-360">
                  <p className="text-xs text-amber-800 leading-relaxed" data-id="element-361">
                    <strong data-id="element-362">Note:</strong> Proceeding will mark the order as
                    partially fulfilled. This will trigger a notification to the
                    customer and the ordering channel.
                  </p>
                </div>

                <div className="flex gap-3 pt-2" data-id="element-363">
                  <button onClick={() => setShowPartialModal(false)} className="flex-1 py-4 text-slate-600 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-364">
                    Go Back
                  </button>
                  <button onClick={() => {
                // Mark remaining items as "Unavailable" or similar to finalize picking
                setItems(prev => prev.map(item => item.status !== 'Picked' ? {
                  ...item,
                  status: 'Unavailable'
                } : item));
                setIsWaitingConfirmation(false);
                setShowPartialModal(false);
                import('sonner').then(({
                  toast
                }) => {
                  toast.warning('Order marked for partial fulfillment');
                });
              }} className="flex-1 py-4 bg-amber-600 text-white font-bold rounded-2xl shadow-lg shadow-amber-200 hover:bg-amber-700 transition-all" data-id="element-365">
                    Confirm Partial
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Rejection/Cancellation Reason Modal */}
      <AnimatePresence data-id="element-366">
        {showRejectModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-367">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-368">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-369">
                <h3 className="font-bold text-slate-900" data-id="element-370">Cancel Order</h3>
                <button onClick={() => setShowRejectModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-371">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-372" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-373">
                <div className="text-center space-y-2" data-id="element-374">
                  <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto text-red-600" data-id="element-375">
                    <AlertCircle className="w-8 h-8" data-id="element-376" />
                  </div>
                  <h4 className="font-bold text-lg text-slate-900" data-id="element-377">
                    Select Reason
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-378">
                    Mandatory for order cancellation.
                  </p>
                </div>

                <div className="space-y-2" data-id="element-379">
                  {rejectionReasons.map(reason => <button key={reason} onClick={() => setRejectionReason(reason)} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-medium transition-all', rejectionReason === reason ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-380">
                      {reason}
                    </button>)}
                </div>

                {rejectionReason === 'Other' && <textarea value={customReason} onChange={e => setCustomReason(e.target.value)} placeholder="Enter custom reason..." className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-red-500 focus:bg-white transition-all text-sm min-h-[100px]" data-id="element-381" />}

                <div className="flex gap-3 pt-2" data-id="element-382">
                  <button onClick={() => setShowRejectModal(false)} className="flex-1 py-4 text-slate-600 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-383">
                    Back
                  </button>
                  <button disabled={!rejectionReason || rejectionReason === 'Other' && !customReason} onClick={() => {
                onUpdateStatus('Cancelled', {
                  rejectionReason: rejectionReason === 'Other' ? customReason : rejectionReason
                });
                setShowRejectModal(false);
              }} className="flex-1 py-4 bg-red-600 text-white font-bold rounded-2xl shadow-lg shadow-red-200 hover:bg-red-700 disabled:opacity-50 disabled:shadow-none transition-all" data-id="element-384">
                    Confirm Cancellation
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Hold Reason Modal */}
      <AnimatePresence data-id="element-385">
        {showHoldModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-386">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-387">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-388">
                <h3 className="font-bold text-slate-900" data-id="element-389">Hold Order</h3>
                <button onClick={() => setShowHoldModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-390">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-391" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-392">
                <div className="text-center space-y-2" data-id="element-393">
                  <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto text-slate-600" data-id="element-394">
                    <Clock className="w-8 h-8" data-id="element-395" />
                  </div>
                  <h4 className="font-bold text-lg text-slate-900" data-id="element-396">
                    Reason for Hold
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-397">
                    Order will be sent to Customer Service.
                  </p>
                </div>

                <div className="space-y-2" data-id="element-398">
                  {['Customer Unreachable', 'Incorrect Address', 'Refused by Customer', 'Other'].map(reason => <button key={reason} onClick={() => setHoldReason(reason)} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-medium transition-all', holdReason === reason ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-399">
                      {reason}
                    </button>)}
                </div>

                <div className="flex gap-3 pt-2" data-id="element-400">
                  <button onClick={() => setShowHoldModal(false)} className="flex-1 py-4 text-slate-600 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-401">
                    Back
                  </button>
                  <button disabled={!holdReason} onClick={() => {
                onUpdateStatus('Held', {
                  holdReason
                });
                setShowHoldModal(false);
              }} className="flex-1 py-4 bg-slate-800 text-white font-bold rounded-2xl shadow-lg shadow-slate-200 hover:bg-slate-900 disabled:opacity-50 disabled:shadow-none transition-all" data-id="element-402">
                    Confirm Hold
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* OTP Verification Modal */}
      <AnimatePresence data-id="element-403">
        {showOtpModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-404">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-405">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-406">
                <h3 className="font-bold text-slate-900" data-id="element-407">OTP Verification</h3>
                <button onClick={() => setShowOtpModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-408">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-409" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-410">
                <div className="text-center space-y-2" data-id="element-411">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto text-blue-600" data-id="element-412">
                    <Smartphone className="w-8 h-8" data-id="element-413" />
                  </div>
                  <h4 className="font-bold text-lg text-slate-900" data-id="element-414">
                    تأكيد الرمز (OTP)
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-415">
                    يرجى إدخال الرمز المكون من 4 أرقام المرسل للعميل للتأكيد.
                  </p>
                </div>

                <div className="space-y-4" data-id="element-416">
                  <div className="relative" data-id="element-417">
                    <input autoFocus type="text" maxLength={4} value={otpValue} onChange={e => setOtpValue(e.target.value)} placeholder="0000" className="w-full text-center py-6 bg-slate-50 border-2 border-slate-100 focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all font-black text-4xl tracking-widest" data-id="element-418" />
                  </div>

                  <div className="flex flex-col items-center gap-3" data-id="element-419">
                    <button disabled={!canResend} onClick={handleResendOtp} className={cn('text-xs font-bold uppercase tracking-wider transition-colors', canResend ? 'text-blue-600 hover:text-blue-700 underline' : 'text-slate-400')} data-id="element-420">
                      {canResend ? 'Resend OTP' : `Resend OTP in ${resendTimer}s`}
                    </button>

                    <button onClick={handleSendWhatsApp} className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-xl text-xs font-bold hover:bg-[#128C7E] transition-all shadow-sm" data-id="element-421">
                      <Smartphone className="w-3.5 h-3.5" data-id="element-422" />
                      Send via WhatsApp
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-2" data-id="element-423">
                  <button onClick={() => setShowOtpModal(false)} className="flex-1 py-4 text-slate-600 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-424">
                    Cancel
                  </button>
                  <button disabled={otpValue.length !== 4} onClick={() => {
                if (otpValue === order.otp) {
                  onUpdateStatus('Delivered', {
                    otpVerified: true
                  });
                  setShowOtpModal(false);
                } else {
                  import('sonner').then(({
                    toast
                  }) => toast.error('Invalid OTP. Please try again.'));
                }
              }} className="flex-1 py-4 bg-blue-600 text-white font-bold rounded-2xl shadow-lg shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 disabled:shadow-none transition-all" data-id="element-425">
                    Verify & Complete
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Prescription Verification Modal */}
      <AnimatePresence data-id="element-426">
        {showRxModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-427">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-428">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-purple-50/50" data-id="element-429">
                <div className="flex items-center gap-3" data-id="element-430">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600" data-id="element-431">
                    <Info className="w-6 h-6" data-id="element-432" />
                  </div>
                  <div data-id="element-433">
                    <h3 className="font-bold text-slate-900" data-id="element-434">
                      Prescription Verification
                    </h3>
                    <p className="text-xs text-slate-500" data-id="element-435">
                      Mandatory for controlled substances
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowRxModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-436">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-437" />
                </button>
              </div>

              <div className="p-8 grid grid-cols-2 gap-8" data-id="element-438">
                <div className="space-y-6" data-id="element-439">
                  <div className="aspect-[3/4] bg-slate-100 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center p-6 text-center group hover:border-purple-300 transition-colors cursor-pointer" data-id="element-440">
                    {order.prescriptionImage ? <img src={order.prescriptionImage} alt="Prescription" className="w-full h-full object-contain rounded-lg" data-id="element-441" /> : <>
                        <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-442">
                          <Globe className="w-8 h-8 text-slate-300" data-id="element-443" />
                        </div>
                        <p className="text-sm font-bold text-slate-900" data-id="element-444">
                          Prescription Image
                        </p>
                        <p className="text-xs text-slate-400 mt-1" data-id="element-445">
                          Click to view or upload scan
                        </p>
                      </>}
                  </div>
                </div>

                <div className="space-y-6" data-id="element-446">
                  <div className="space-y-4" data-id="element-447">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest" data-id="element-448">
                      Verification Checklist
                    </h4>
                    <div className="space-y-3" data-id="element-449">
                      {['Patient name matches order', 'Doctor signature & stamp present', 'Date is within validity period', 'Items & dosages match order'].map(check => <label key={check} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-white hover:border-purple-200 transition-all group" data-id="element-450">
                          <input type="checkbox" className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500" data-id="element-451" />
                          <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900" data-id="element-452">
                            {check}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl" data-id="element-453">
                    <p className="text-[10px] text-amber-800 leading-relaxed font-medium" data-id="element-454">
                      <strong data-id="element-455">Warning:</strong> Dispensing controlled substances
                      without valid verification is a violation of MOH
                      regulations.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3" data-id="element-456">
                <button onClick={() => setShowRxModal(false)} className="flex-1 py-4 text-slate-600 font-bold hover:bg-white rounded-2xl transition-colors border border-transparent hover:border-slate-200" data-id="element-457">
                  Cancel
                </button>
                <button onClick={() => {
              onUpdateStatus(order.status, {
                prescriptionVerified: true
              });
              setShowRxModal(false);
              import('sonner').then(({
                toast
              }) => toast.success('Prescription verified successfully'));
            }} className="flex-1 py-4 bg-purple-600 text-white font-bold rounded-2xl shadow-lg shadow-purple-200 hover:bg-purple-700 transition-all" data-id="element-458">
                  Confirm Verification
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}